import { parseChecklistProcedures } from '@/domain/models/Checklist'

import { ChecklistTemplate } from './IChecklistTemplateRepository'

import { isRecord, toNonEmptyTrimmedString } from '@/utils/safe'

/**
 * Normalizes one raw catalogue entry, or returns `null` when it cannot be trusted.
 *
 * The catalogue is edited outside the app, so every field is coerced: `id`, `name`,
 * `aircraftModelId` and `procedures` are all required, and a template whose `procedures` produce no
 * usable procedure is dropped instead of reaching a screen as an empty template.
 *
 * Procedure parsing is delegated to the domain's `parseChecklistProcedures`, the same parser the
 * user's own checklists go through — the pilotapp keeps a second copy of it in its template
 * repository, and one parser is what keeps a template from being read differently than the checklist
 * instantiated out of it. The only visible difference is the synthetic id of a procedure that ships
 * without one (`procedure:0` instead of the pilotapp's `<templateId>:procedure:0`), and those ids are
 * regenerated when the template is instantiated anyway.
 */
export const normalizeTemplate = (template: unknown): ChecklistTemplate | null => {
  if (!isRecord(template)) return null

  const id = toNonEmptyTrimmedString(template.id)
  const name = toNonEmptyTrimmedString(template.name)
  const aircraftModelId = toNonEmptyTrimmedString(template.aircraftModelId)
  const proceduresPayload = toNonEmptyTrimmedString(template.procedures)

  if (!id || !name || !aircraftModelId || !proceduresPayload) return null

  const procedures = parseChecklistProcedures(proceduresPayload)
  if (!procedures.length) return null

  return {
    id,
    name,
    aircraftModelId,
    procedures
  }
}

export const normalizeTemplates = (payload: unknown): ChecklistTemplate[] => {
  if (!Array.isArray(payload)) return []

  return payload.map((template) => normalizeTemplate(template)).filter(Boolean) as ChecklistTemplate[]
}
